import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { MatChip } from '@angular/material/chips';
import { TranslocoPipe } from '@jsverse/transloco';
import { Assignment, AssignmentWithRelations } from '../../../../../api/app-types/assignment';
import { REPORT_TYPE, ReportDocument } from '../../../../../api/app-types/report-document.app';
import {
	DOCUMENT_CATEGORY,
	DocumentEntry,
	DocumentFile,
	DocumentNavItem,
} from '../../../../../api/app-types/document.app';
import { User } from '../../../../../api/app-types/user.app';
import { REPORT_API } from '../../../../../api/services/report.service';
import { ActionMenuItem, LvrActionMenu } from '../../../../../core/components/action-menu/action-menu';
import { LvrHeading } from '../../../../../core/components/heading/heading';
import { AccessService } from '../../../../../core/services/access.service';
import { LOADING } from '../../../../../core/services/loading.service';
import { NOTIFICATION_SERVICE } from '../../../../../shared/notification/notification';
import { PaginatedResponse } from '../../../../../shared/pagination/paginated-response';
import { ClientResolved } from '../../../../../shared/routing/resolvers/client.resolver';
import { RouteInjection } from '../../../../../shared/routing/route-injection.class';
import { DB_PHASES } from '../../../../../api/app-types/phase.app';
import { LvrPaddingDirective } from '../../../../../shared/padding/padding.directive';
import { LvrDocumentItem } from '../../../../../core/components/document-item/document-item';
import { hasPermission } from '../../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../../shared/storage/session-manager.service';
import { TENANT_FEATURES } from '../../../../../core/services/tenant-feature.service';
import { downloadDocumentFiles } from '../../../../../shared/helpers/document-download.func';
import { REPRESENTATION_MODE } from '../../../../../core/services/representation-mode.service';
import { RECORD_MANAGER } from '../../../../../core/services/record.provide';
import { Client, RestrictedClient } from '../../../../../api/app-types/client.app';
import { NavCarousel } from '../../../../../core/components/nav-carousel/nav-carousel';
import { LvrButton } from '../../../../../core/components/lvr-button/lvr-button';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
	selector: 'lvr-additional-assignment-details',
	templateUrl: './additional-assignment-details.html',
	styleUrl: './additional-assignment-details.scss',
	imports: [
		DatePipe,
		LvrHeading,
		TranslocoPipe,
		LvrActionMenu,
		MatChip,
		LvrPaddingDirective,
		LvrDocumentItem,
		NavCarousel,
		LvrButton,
		MatTooltip,
	],
})
export class AdditionalAssignmentDetails extends RouteInjection {
	private readonly api = inject(REPORT_API);
	private readonly access = inject(AccessService);
	private readonly loading = inject(LOADING);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly representationMode = inject(REPRESENTATION_MODE);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved>('client');
	private readonly reportsPage = this.resolveDataAsSignal<PaginatedResponse<ReportDocument>>('reports');
	private readonly user = this.resolveDataAsSignal<User>('user');
	private readonly client = this.resolveDataAsSignal<ClientResolved>('client');
	private readonly routeAssignment = this.resolveDataAsSignal<AssignmentWithRelations>('assignment');
	public readonly assignmentInput = input<AssignmentWithRelations | undefined>(undefined);
	public readonly assignment = computed(() => this.assignmentInput() ?? this.routeAssignment());
	public readonly PHASE4 = DB_PHASES.P4;
	public readonly PHASE1 = DB_PHASES.P1;
	public readonly showHeading = input<boolean>(true);
	public readonly showReports = input<boolean>(true);
	public readonly showEditButton = input<boolean | undefined>(undefined);
	private readonly lastTitle = signal<string | null>(null);
	public readonly backLink = computed(() =>
		this.router.serializeUrl(this.router.createUrlTree(['../../'], { relativeTo: this.route })),
	);
	public readonly carouselElements = computed(() => {
		const assignment = this.assignment();
		const id = String(assignment?.id ?? '');

		return [
			{
				id,
				label: assignment?.phase?.remark ?? id,
			},
		];
	});

	private readonly recordTitle = computed(() => {
		const c = this.clientResolved();
		const v = c?.value as Client | RestrictedClient;
		const name = v?.name?.trim() ?? '';
		const firstName = v?.firstName?.trim() ?? '';
		if (name && firstName) return `${name}, ${firstName}`;
		if (name) return name;
		if (firstName) return firstName;
		return 'Klient';
	});

	public constructor() {
		super();
		effect(() => {
			const title = this.recordTitle();
			if (this.lastTitle() === title) return;
			// To avoid effect loop
			untracked(() => this.recordManager.updateRecordTitleOfActiveRecord(title));
			this.lastTitle.set(title);
		});
	}

	private clientId() {
		return this.client()?.value.id ?? this.route.snapshot.params['id'];
	}

	private assignmentId() {
		return this.assignment()?.id ?? this.route.snapshot.params['assignmentId'];
	}

	private isRepresentingUser(userId?: number | string | null): boolean {
		const representedUserId = this.representationMode.mode().representedUserId;

		if (representedUserId == null || userId == null) return false;

		return String(representedUserId) === String(userId);
	}

	private isRepresentingAdditionalAssignmentOwner(assignment: Assignment): boolean {
		const owner = assignment.ownerOfAdditionalAssignment ?? assignment.createdByUser;

		return this.isRepresentingUser(owner?.id);
	}

	public readonly canEditAssignment = computed(() => {
		const assignment = this.assignment();
		const user = this.user();

		if (!assignment || !user || assignment.phaseId === DB_PHASES.P4) return false;

		if (!assignment.isAdditionalAssignment) return false;

		return (
			(this.access.isAdditionalOwner(assignment, user) && !this.representationMode.isActive()) ||
			(this.representationMode.isActive() && this.isRepresentingAdditionalAssignmentOwner(assignment))
		);
	});
	public readonly resolvedShowEditButton = computed(() => {
		// For tenants without additional assignments, hide the edit affordance completely.
		if (!this.tenantFeatures.additionalAssignmentEnabled()) {
			return false;
		}

		const override = this.showEditButton();
		if (override !== undefined) return override;
		return this.assignment()?.phaseId !== this.PHASE4 && this.canEditAssignment();
	});

	public readonly startReports = computed(() =>
		(this.reportsPage()?.entities ?? []).filter((r) => r.categoryReport.id === REPORT_TYPE.START_REPORT),
	);
	public readonly impedimentReports = computed(() =>
		(this.reportsPage()?.entities ?? []).filter((r) => r.categoryReport.id === REPORT_TYPE.IMPEDIMENT_REPORT),
	);
	public readonly endReports = computed(() =>
		(this.reportsPage()?.entities ?? []).filter((r) => r.categoryReport.id === REPORT_TYPE.END_REPORT),
	);
	public readonly reports = computed(() => [...this.startReports(), ...this.impedimentReports(), ...this.endReports()]);
	public readonly canCreateStartReport = computed(() => {
		const assignment = this.assignment();
		if (!assignment) return false;
		return this.startReports().length === 0 && assignment.phaseId === DB_PHASES.P1;
	});
	public readonly canCreateImpedimentReport = computed(() => {
		const assignment = this.assignment();
		if (!assignment) return false;
		return assignment.phaseId === DB_PHASES.P3 && this.impedimentReports().length === 0;
	});
	public readonly canCreateEndReport = computed(() => {
		const assignment = this.assignment();
		if (!assignment) return false;
		return assignment.phaseId === DB_PHASES.P3 && this.endReports().length === 0;
	});
	public readonly canCreateReportsPermission = hasPermission(this.session.permissions, 'Reports.Create');
	public readonly canReadReportsPermission = hasPermission(this.session.permissions, 'Reports.Read');
	public readonly canEditReportsPermission = hasPermission(this.session.permissions, 'Reports.Edit');
	public readonly canReadAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Read');

	private readonly loadingLabel = 'base.button.creating_report';
	private readonly showLoadingStartReport = this.loading.isCurrentlyLoading(
		'additional-assignment.routeToStartReport',
		'requestKey',
	);
	private readonly showLoadingImpedimentReport = this.loading.isCurrentlyLoading(
		'additional-assignment.routeToImpedimentReport',
		'requestKey',
	);
	private readonly showLoadingEndReport = this.loading.isCurrentlyLoading(
		'additional-assignment.routeToEndReport',
		'requestKey',
	);

	public readonly menuItems = computed<ActionMenuItem[]>(() => [
		{
			label: 'Anfangsbericht',
			onClick: () => this.routeToStartReport(),
			icon: 'add',
			disabled: !this.canCreateReportsPermission() || !this.canCreateStartReport() || !this.canEditAssignment(),
			loadingLabel: this.loadingLabel,
			showLoading: this.showLoadingStartReport(),
		},
		{
			label: 'Sachstandsbericht',
			onClick: () => this.routeToImpedimentReport(),
			icon: 'add',
			disabled: !this.canCreateReportsPermission() || !this.canCreateImpedimentReport() || !this.canEditAssignment(),
			loadingLabel: this.loadingLabel,
			showLoading: this.showLoadingImpedimentReport(),
		},
		{
			label: 'Abschlussbericht',
			onClick: () => this.routeToEndReport(),
			icon: 'add',
			disabled: !this.canCreateReportsPermission() || !this.canCreateEndReport() || !this.canEditAssignment(),
			loadingLabel: this.loadingLabel,
			showLoading: this.showLoadingEndReport(),
		},
	]);

	public openEdit() {
		if (!this.canEditAssignment()) return;
		this.router.navigate(['../', 'edit-additional'], { relativeTo: this.route });
	}

	public openReport(report: ReportDocument) {
		if (!this.canReadReportsPermission()) return;
		if ((report.documents ?? []).length > 0) {
			downloadDocumentFiles(report.documents ?? []);
			return;
		}
		const clientId = this.clientId();
		const assignmentId = this.assignmentId();
		if (!clientId || !assignmentId) return;
		if (report.categoryReport.id === REPORT_TYPE.IMPEDIMENT_REPORT) {
			this.router.navigate(['/desk/client', clientId, 'assignments', assignmentId, 'report', 'impediment', report.id]);
			return;
		}
		if (report.categoryReport.id === REPORT_TYPE.END_REPORT) {
			this.router.navigate(['/desk/client', clientId, 'assignments', assignmentId, 'end-report', report.id]);
			return;
		}
		this.router.navigate(['/desk/client', clientId, 'assignments', assignmentId, 'report', report.id]);
	}

	public onFileClick(file: DocumentFile) {
		if (!this.canReadAssignmentDocuments()) return;
		const assignment = this.assignment();
		if (!assignment) return;
		const navItems = this.buildAssignmentNavItems(assignment.documents ?? []);
		this.router.navigate(['/desk/client', this.clientId(), 'documents', file.id], { state: { navItems } });
	}

	public buildDocumentEntryFromAssignment(doc: DocumentFile) {
		const assignment = this.assignment();
		if (!assignment) return;
		const createdAt = this.ensureDate(assignment.careStartDate ?? assignment.phaseStart ?? new Date());
		const documentEntry: DocumentEntry = {
			id: assignment.id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.ASSIGNMENT,
			createdAt,
			externalFileReference: assignment.documents ?? [],
			isReport: false,
			isFrozen: false,
			assignmentId: assignment.id,
		};
		return documentEntry;
	}

	private buildAssignmentNavItems(files: DocumentFile[]): DocumentNavItem[] {
		const assignment = this.assignment();
		if (!assignment) return [];
		const createdAt = this.ensureDate(assignment.careStartDate ?? assignment.phaseStart ?? new Date());
		return files.map((file) => ({
			file,
			entryId: assignment.id,
			entryName: '',
			category: DOCUMENT_CATEGORY.ASSIGNMENT,
			createdAt,
			isReport: false,
			isFrozen: false,
		}));
	}

	private ensureDate(value: Date | string): Date {
		if (value instanceof Date) return value;
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
	}

	private openNotification(msg: string) {
		this.ntfy.addNotification({
			message: msg,
			id: window.crypto.randomUUID(),
			type: 'error',
			showToast: true,
			timestamp: new Date(),
		});
	}

	private async routeToStartReport() {
		if (this.showLoadingStartReport()) return;

		const assignment = this.assignment();
		const clientId = this.clientId();
		if (!assignment?.typeOfCareId) {
			this.openNotification('Dem Auftrag fehlt die Angabe der Betreuungsart.');
			return;
		}
		const templates = await this.api.getDocumentTemplates(assignment.typeOfCareId, undefined, undefined, true);
		const template = templates?.entities.find((t) => {
			const categoryId = t.category?.id;
			if (!categoryId) {
				console.error('No template category ID');
				return;
			}
			return categoryId === REPORT_TYPE.START_REPORT;
		});
		if (!template) {
			this.openNotification('Es sind keine Berichtsvorlagen fuer diese Betreuungsart der Zuweisung hinterlegt.');
			return;
		}
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'additional-assignment.routeToStartReport');
		try {
			const result = await this.api.createReportDocument(template.id, assignment.id);
			if (!result) {
				this.openNotification('Der Anfangsbericht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
				return;
			}
			await this.router.navigate(['/desk/client', clientId, 'assignments', assignment.id, 'report', result]);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async routeToImpedimentReport() {
		if (this.showLoadingImpedimentReport()) return;

		const assignment = this.assignment();
		const clientId = this.clientId();
		if (!assignment || !clientId) return;

		const uuid = this.loading.setLoadingState(
			window.crypto.randomUUID(),
			'additional-assignment.routeToImpedimentReport',
		);
		try {
			const templates = await this.api.getDocumentTemplates(assignment.typeOfCareId, undefined, undefined, true);
			const impedimentTemplate = templates?.entities.find((t) => {
				const categoryId = t.category?.id;
				if (!categoryId) {
					console.error('No category ID');
					return;
				}
				return categoryId === REPORT_TYPE.IMPEDIMENT_REPORT;
			});

			if (!impedimentTemplate) {
				this.openNotification('Es sind keine Berichtsvorlagen fuer den Sachstandsbericht hinterlegt.');
				return;
			}
			const result = await this.api.createReportDocument(impedimentTemplate.id, assignment.id);
			await this.router.navigate(
				['/desk/client', clientId, 'assignments', assignment.id, 'report', 'impediment', result],
				{
					queryParams: {
						dueDate: assignment.impedimentReportDueDate?.getISODateStringWithoutTime(),
					},
				},
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	private async routeToEndReport() {
		if (this.showLoadingEndReport()) return;

		const assignment = this.assignment();
		const clientId = this.clientId();
		if (!assignment?.typeOfCareId || !clientId) {
			this.openNotification('Dem Auftrag fehlt die Angabe der Betreuungsart.');
			return;
		}
		const templates = await this.api.getDocumentTemplates(assignment.typeOfCareId, undefined, undefined, true);
		const template = templates?.entities.find((t) => {
			const categoryId = t.category?.id;
			if (!categoryId) {
				console.error('No category ID');
				return;
			}
			return categoryId === REPORT_TYPE.END_REPORT;
		});
		if (!template) {
			this.openNotification('Es sind keine Berichtsvorlagen fuer diese Betreuungsart der Zuweisung hinterlegt.');
			return;
		}
		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), 'additional-assignment.routeToEndReport');
		try {
			const result = await this.api.createReportDocument(template.id, assignment.id);
			if (!result) {
				this.openNotification('Der Abschlussbericht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
				return;
			}
			await this.router.navigate(['/desk/client', clientId, 'assignments', assignment.id, 'end-report', result]);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}

	public openMail(recipient: string) {
		const assignmentGuid = this.assignment()?.guid ?? '-';
		const url = encodeURI(window.location.href);
		const body = `Auftrag Nummer: ${assignmentGuid}\nLink: ${url}`;
		const href = this.buildMailto(recipient, `KlifdWeb: Auftrag - Nummer: ${assignmentGuid}`, body);
		window.location.href = href;
	}

	/**
	 * Builds a safe mailto U with properly encoded subject and body.
	 * Encodes special characters to prevent URL breakage (spaces, &, ?, etc.)
	 */
	private buildMailto(recipient: string, subject: string, body: string): string {
		const encoded = (s: string) => encodeURIComponent(s ?? '');
		const to = encodeURIComponent(recipient);
		return `mailto:${to}?subject=${encoded(subject)}&body=${encoded(body)}`;
	}
}
